/**
 * 页面元素分批入场：仅壳层落稳后、新插入的列表行淡入。
 * 首屏 / 侧栏 / 玩法卡跟壳层一起滑，禁止「先藏再显」造成卡顿。
 */
import { PAGE_ENTER_TIMING } from '@/config/preload';
import { prefersReducedMotion } from '@/utils/motion-pref';

/** 仅晚到的列表行；玩法卡 / 价目表不挂 */
const AUTO_REVEAL_SEL = '.rank-entry';

export interface PageRevealApi {
  prepare: () => void;
  start: (opts?: { immediate?: boolean }) => void;
  destroy: () => void;
}

function tagDynamic(el: HTMLElement, index: number): void {
  if (el.hasAttribute('data-reveal')) return;
  if (el.closest('.brand-rail')) return;
  // 入场未完成时不挂：跟壳层同帧出现
  if (!document.body.classList.contains('is-enter-done')) return;
  el.setAttribute('data-reveal', 'fade');
  el.setAttribute('data-reveal-delay', String(20 + (index % 8) * 40));
}

function collectTargets(root: ParentNode = document): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]')).filter(
    (el) => !el.closest('.brand-rail'),
  );
}

export function usePageReveal(): PageRevealApi {
  let mo: MutationObserver | null = null;
  let timers: number[] = [];
  let seq = 0;

  const prepareDynamic = (root: ParentNode = document) => {
    root.querySelectorAll<HTMLElement>(AUTO_REVEAL_SEL).forEach((el) => {
      tagDynamic(el, seq++);
    });
  };

  const prepare = () => {
    document.documentElement.style.setProperty(
      '--reveal-dur',
      `${PAGE_ENTER_TIMING.revealMs / 1000}s`,
    );
  };

  const revealOne = (el: HTMLElement, immediate: boolean) => {
    const delay = immediate ? 0 : Number(el.dataset.revealDelay || 0);
    const id = window.setTimeout(() => {
      el.classList.add('is-revealed');
    }, delay);
    timers.push(id);
  };

  const start = (opts?: { immediate?: boolean }) => {
    timers.forEach((t) => clearTimeout(t));
    timers = [];
    mo?.disconnect();
    mo = null;

    const immediate = !!opts?.immediate || prefersReducedMotion();

    // 入场已完成：此时才允许给新节点挂淡入
    prepareDynamic();
    collectTargets().forEach((el) => {
      if (el.dataset.revealBound === '1') return;
      el.dataset.revealBound = '1';
      revealOne(el, immediate);
    });

    document.querySelectorAll<HTMLElement>('.brand-rail [data-reveal]').forEach((el) => {
      el.classList.add('is-revealed');
    });

    mo = new MutationObserver(() => {
      const before = seq;
      prepareDynamic();
      if (seq === before) return;
      collectTargets().forEach((el) => {
        if (el.dataset.revealBound === '1') return;
        el.dataset.revealBound = '1';
        revealOne(el, immediate);
      });
    });

    for (const id of ['playGrid', 'probabilityGrid', 'rbList', 'rankRoot']) {
      const box = document.getElementById(id);
      if (box) mo.observe(box, { childList: true, subtree: true });
    }
  };

  const destroy = () => {
    timers.forEach((t) => clearTimeout(t));
    timers = [];
    mo?.disconnect();
    mo = null;
  };

  return { prepare, start, destroy };
}
